import type { QuestionFigure } from '@padcanvas/shared';
import type { ApiResponse, Module, ModuleImportResult, Question, QuestionComment, QuestionListResponse } from '../types';
import { getErrorMessage } from '../types';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin, getAuthHeaders, handleApiResponse } from './fetchHelper';
import { buildQuestionListQuery, type QuestionListQueryFilters } from '@shared-ui/questions-page';
import { createImportApi } from '@shared-ui/import-questions-dialog';
import type { QuestionRevision, QuestionRevisionSummary } from '@shared-ui/question-history';
import type { PartRequirement } from '@padcanvas/shared';
import type { QuestionSourceApi, QuestionSourceCropResult, QuestionSourceInfo } from '@shared-ui/question-edit-form';

/** Question content needed to render a printable question-paper view — stem,
 *  structured parts, choice/K-of-N requirement, MCQ options and diagram. No
 *  answers or solutions (a question paper shows questions only). */
export interface PaperQuestion {
  id: string;
  questionText: string;
  subject?: string;
  questionType: string | null;
  parts?: Array<{ partId: string; statement?: string }> | null;
  requirement?: PartRequirement;
  options?: { label: string; text: string; imageUrl?: string | null }[] | null;
  questionDiagramUrl?: string | null;
  figures?: QuestionFigure[] | null;
  /** Computed total display marks (single-part marks, or sum of STRUCTURED
   *  part marks). Admin paper views always show it. */
  marks?: number;
  /** Lifecycle state — returned for admin requests. 'draft' questions shouldn't
   *  ship in a published public test, so the UI flags them. */
  importStatus?: 'draft' | 'live' | null;
}

/** One book node (chapter/section) where a question's module is mapped. */
export interface QuestionCurriculumNode {
  placementId: string;
  bookLevel: string;
  number: string;
  displayName: string;
  isPrimary: boolean;
}

/** A curriculum this question belongs to, with the node(s) where it appears. */
export interface QuestionCurriculumMembership {
  curriculumId: string;
  name: string;
  board: string;
  subject: string;
  grade?: string;
  examTrack: boolean;
  isActive: boolean;
  ownerType?: string;
  teacherId?: string;
  gradeMatch: boolean;
  /** question.targetExams covers this curriculum's board (informational, not a filter). */
  examMatch: boolean;
  nodes: QuestionCurriculumNode[];
}

export interface QuestionCurriculaResponse {
  questionId: string;
  module: { id: string; name: string } | null;
  curricula: QuestionCurriculumMembership[];
}

export interface CurriculaPreviewResponse {
  moduleId: string | null;
  curricula: QuestionCurriculumMembership[];
}

export interface DeletionsListItem {
  questionId: string;
  seq: number;
  subject?: string;
  questionText?: string;
  questionType?: string;
  teacherId?: string;
  changedBy: string;
  changedByName?: string;
  changedByUserType: string;
  changedAt: string;
  changeNote?: string;
}

/** Bound import API — both real-run and dry-run calls flow through
 *  `fetchWithAdmin`, sharing its 401/error handling with the rest of the
 *  admin client. The actual URL routing + payload assembly lives in
 *  shared-ui so teacher-app can reuse it without duplicating the HTTP layer. */
export const importApi = createImportApi(fetchWithAdmin);

export const questionApi = {
  getQuestionList: (filters?: QuestionListQueryFilters): Promise<QuestionListResponse> => {
    const qs = buildQuestionListQuery({ pageSize: 200, ...filters });
    return fetchWithAdmin<QuestionListResponse>(`/question-list?${qs}`);
  },

  exportQuestions: (filters: Record<string, string | number>, embedImages?: boolean) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== '' && v !== undefined && k !== 'maxCount') params.append(k, String(v));
    }
    params.append('maxCount', '1000');
    if (embedImages) params.append('embedImages', 'true');
    return fetchWithAdmin<{ questions: Record<string, unknown>[]; images?: Record<string, { data: string; mimeType: string }>; warnings?: string[] }>(`/console/questions/export?${params.toString()}`);
  },

  exportQuestionsByIds: (ids: string[], embedImages?: boolean) => {
    return fetchWithAdmin<{ questions: Record<string, unknown>[]; images?: Record<string, { data: string; mimeType: string }>; warnings?: string[] }>(
      `/console/questions/export-by-ids`,
      { method: 'POST', body: JSON.stringify({ ids, embedImages: !!embedImages }) },
    );
  },

  getQuestionById: (id: string) => fetchWithAdmin<Question>(`/questions/${id}`),

  /** Which curricula this question is part of (admin-only reverse lookup on
   *  its canonical module). See functions getQuestionCurricula.ts. */
  getQuestionCurricula: (id: string) =>
    fetchWithAdmin<QuestionCurriculaResponse>(`/questions/${encodeURIComponent(id)}/curricula`),

  /** Live "which curricula will this question be part of?" from unsaved edit-form
   *  values (module + class + target exams). See functions getCurriculaPreview.ts. */
  getCurriculaPreview: (input: { moduleId?: string | null; classes?: string[]; targetExams?: string[] }) =>
    fetchWithAdmin<CurriculaPreviewResponse>(`/questions/curricula-preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Batch-fetch question content (one request) for the printable paper view.
   *  Preserves no order — callers re-order by their own id list. */
  getPaperQuestions: (ids: string[]) =>
    fetchWithAdmin<{ questions: PaperQuestion[]; total: number }>(
      `/question-list?${buildQuestionListQuery({
        ids: ids.join(','),
        includeFields: 'options,questionDiagramUrl,marks',
        pageSize: ids.length,
      })}`,
    ),

  saveQuestion: async (question: Question): Promise<Question> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/questions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(question),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    // Always try to parse the response body to get error details
    const data: ApiResponse<Question> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as Question;
  },

  // Append a comment/annotation to a question. Open to any authenticated user;
  // commentBy / commentByType are derived server-side from the token.
  addComment: async (
    questionId: string,
    body: { commentText: string; commentType?: string },
  ): Promise<QuestionComment[]> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/questions/${encodeURIComponent(questionId)}/comments`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ comment: QuestionComment; comments: QuestionComment[] }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }
    return data.data!.comments;
  },

  bulkEditQuestions: async (ids: string[], updates: {
    targetExams?: { mode: 'merge' | 'override'; values: string[] };
    class?: { mode: 'merge' | 'override'; values: string[] };
    difficulty?: 'easy' | 'medium' | 'hard';
    importStatus?: 'draft' | 'live';
    questionReadiness?: number;
  }): Promise<{ updated: number }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/questions/bulk-edit`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids, updates }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ updated: number }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { updated: number };
  },

  bulkDeleteQuestions: async (ids: string[]): Promise<{ deleted: number; archived: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/questions/bulk-delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<{ deleted: number; archived: number }> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as { deleted: number; archived: number };
  },

  bulkPublishQuestions: async (ids: string[]): Promise<{ published: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/questions/bulk-publish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<{ published: number }> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as { published: number };
  },

  bulkUnpublishQuestions: async (ids: string[]): Promise<{ unpublished: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/questions/bulk-unpublish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<{ unpublished: number }> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as { unpublished: number };
  },

  checkQuestionAttempts: async (ids: string[]): Promise<{ attemptedQuestionIds: string[] }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/questions/check-attempts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<{ attemptedQuestionIds: string[] }> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as { attemptedQuestionIds: string[] };
  },

  fixLatex: async (fields: {
    questionText?: string;
    options?: { label: string; text: string }[];
    solutions?: { style?: string; proficiencyLevel?: string; steps: string; expectedTimeSeconds?: number }[];
    hints?: string[];
    correctAnswer?: string;
  }) => {
    const response = await fetch(`${API_BASE}/console/questions/fix-latex`, {
      method: 'POST',
      headers: getAuthHeaders('application/json'),
      body: JSON.stringify(fields),
    });
    return handleApiResponse<typeof fields>(response);
  },

  // Re-cut a question image from its source paper.
  // See docs/FeatureDesigns/question-image-recrop-target.md.
  getQuestionSource: async (questionId: string): Promise<QuestionSourceInfo> => {
    const response = await fetch(`${API_BASE}/console/questions/${encodeURIComponent(questionId)}/source`, {
      headers: getAuthHeaders('application/json'),
    });
    return handleApiResponse<QuestionSourceInfo>(response);
  },

  cropQuestionImageFromSource: async (
    questionId: string,
    body: Parameters<QuestionSourceApi['cropFromSource']>[1],
  ): Promise<QuestionSourceCropResult> => {
    const response = await fetch(`${API_BASE}/console/questions/${encodeURIComponent(questionId)}/source-crop`, {
      method: 'POST',
      headers: getAuthHeaders('application/json'),
      body: JSON.stringify(body),
    });
    return handleApiResponse<QuestionSourceCropResult>(response);
  },

  uploadQuestionImage: async (imageBase64: string, contentType?: string): Promise<{ url: string }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageBase64, contentType }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ url: string }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { url: string };
  },

  // ── Version history ──────────────────────────────────────────────────

  getRevisions: (questionId: string) =>
    fetchWithAdmin<{ total: number; revisions: QuestionRevisionSummary[] }>(
      `/console/questions/${encodeURIComponent(questionId)}/revisions`
    ),

  getRevision: (questionId: string, seq: number) =>
    fetchWithAdmin<{ revision: QuestionRevision }>(
      `/console/questions/${encodeURIComponent(questionId)}/revisions/${seq}`
    ),

  restoreRevision: async (questionId: string, seq: number, changeNote?: string): Promise<void> => {
    await fetchWithAdmin<{ question: Question; seq: number }>(
      `/console/questions/${encodeURIComponent(questionId)}/revisions/${seq}/restore`,
      {
        method: 'POST',
        body: JSON.stringify(changeNote ? { changeNote } : {}),
      }
    );
  },

  /** Thumbs on the QUESTION itself. A thumbs-down also opens a User Feedback
   *  item; a thumbs-up is only recorded on the question. */
  rateQuestion: (questionId: string, rating: 'positive' | 'negative', comment?: string) =>
    fetchWithAdmin<{ userFeedback: unknown[] }>(
      `/console/questions/${encodeURIComponent(questionId)}/feedback`,
      { method: 'POST', body: JSON.stringify({ rating, ...(comment ? { comment } : {}) }) }
    ),

  createAssignmentFromImport: (teacherId: string, title: string, questionIds: string[]) =>
    fetchWithAdmin<{ questionSet: { id: string; title: string; totalQuestions: number } }>(
      '/question-set',
      { method: 'POST', body: JSON.stringify({
        action: 'create',
        title,
        questionIds,
        visibility: 'private',
        type: 'practice-set',
        onBehalfOfTeacherId: teacherId,
      }) },
    ),

  getDeletions: (params: { since: string; until?: string; subject?: string }) => {
    const qs = new URLSearchParams();
    qs.set('since', params.since);
    if (params.until) qs.set('until', params.until);
    if (params.subject) qs.set('subject', params.subject);
    return fetchWithAdmin<{ total: number; deletions: DeletionsListItem[] }>(
      `/console/questions/deletions?${qs.toString()}`
    );
  },

  // Module CRUD operations
  getModules: (params?: { subject?: string; grade?: string; withCounts?: boolean; full?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.subject) searchParams.set('subject', params.subject);
    if (params?.grade) searchParams.set('grade', params.grade);
    if (params?.withCounts) searchParams.set('withCounts', 'true');
    if (params?.full) searchParams.set('full', 'true');
    const query = searchParams.toString();
    return fetchWithAdmin<{ modules: Module[] }>(
      `/modules${query ? `?${query}` : ''}`
    );
  },

  getModule: (id: string) => fetchWithAdmin<Module>(`/modules/${id}`),

  saveModule: async (module: Partial<Module>): Promise<Module> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/modules`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(module),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<Module> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as Module;
  },

  deleteModule: async (id: string): Promise<{ deleted: boolean }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/modules/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: boolean }> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as { deleted: boolean };
  },

  // Module Import/Export
  exportModules: () => fetchWithAdmin<{ exportedAt: string; modules: Module[] }>('/modules/export'),

  importModules: (modules: Partial<Module>[], dryRun: boolean) =>
    fetchWithAdmin<ModuleImportResult>('/modules/import', {
      method: 'POST',
      body: JSON.stringify({ modules, dryRun }),
    }),
};
